import { useState, useEffect } from "react";
import { Coins, Trophy, Gift, Lock, Info, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const WALLET_ORDER = ["entry_token", "prize", "bonus", "locked"];

const WALLET_META = {
  entry_token: {
    icon: Coins,
    accent: "cyan-400",
    tint: "from-cyan-500/15 via-cyan-500/5 to-transparent border-cyan-400/40",
    tag: "PARA JOGAR",
    kind: "spendable",
  },
  prize: {
    icon: Trophy,
    accent: "emerald-300",
    tint: "from-emerald-500/15 via-emerald-500/5 to-transparent border-emerald-400/40",
    tag: "SACÁVEL",
    kind: "withdrawable",
  },
  bonus: {
    icon: Gift,
    accent: "fuchsia-300",
    tint: "from-fuchsia-500/15 via-fuchsia-500/5 to-transparent border-fuchsia-400/40",
    tag: "PROMO",
    kind: "bonus",
  },
  locked: {
    icon: Lock,
    accent: "amber-300",
    tint: "from-amber-500/15 via-amber-500/5 to-transparent border-amber-400/40",
    tag: "EM ANÁLISE",
    kind: "reserved",
  },
};

const TOOLTIPS = {
  entry_token:
    "Entry Tokens (1 token = 1 USD) são destinados apenas para inscrições em torneios. Depósitos entram aqui automaticamente. NÃO são sacáveis.",
  prize:
    "Saldo ganho em torneios. Este é o ÚNICO saldo sacável em USDT. Prêmios são creditados aqui automaticamente quando você entra no Top 10.",
  bonus:
    "Créditos promocionais (cupons, eventos, compensações). Podem ou não ser sacáveis dependendo da configuração da plataforma.",
  locked:
    "Saldo temporariamente bloqueado: inscrição confirmada em torneio, saque pendente ou análise antifraude. Volta pra sua carteira original em caso de cancelamento.",
};

function fmt(cents) {
  if (cents == null) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

function WalletCard({ wtype, data }) {
  const meta = WALLET_META[wtype];
  const Icon = meta.icon;
  const [tipOpen, setTipOpen] = useState(false);

  return (
    <div
      data-testid={`wallet-card-${wtype}`}
      className={`relative rounded-sm border bg-gradient-to-br ${meta.tint} p-4 flex flex-col gap-2 min-h-[130px]`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 text-${meta.accent}`} />
          <span className={`font-mono text-[10px] tracking-[0.3em] text-${meta.accent}`}>
            {data.label?.toUpperCase() || wtype.toUpperCase()}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setTipOpen((v) => !v)}
          onBlur={() => setTipOpen(false)}
          data-testid={`wallet-card-info-${wtype}`}
          className="text-muted-foreground hover:text-white transition p-0.5"
          aria-label={`Info sobre ${data.label}`}
        >
          <Info className="h-3 w-3" />
        </button>
      </div>

      <div className="font-display font-black tracking-tighter text-white text-2xl md:text-3xl leading-none">
        {fmt(data.amount_cents)}
      </div>

      <div className="flex items-center justify-between mt-auto">
        <span className={`font-mono text-[9px] tracking-widest text-${meta.accent}/80`}>
          {meta.tag}
        </span>
        <span className="font-mono text-[9px] tracking-widest text-muted-foreground">
          {data.withdrawable ? "✓ SACÁVEL" : "✗ NÃO SACA"}
        </span>
      </div>

      {tipOpen && (
        <div
          data-testid={`wallet-tooltip-${wtype}`}
          className={`absolute z-30 top-full mt-2 left-0 right-0 rounded-sm border border-${meta.accent}/60 bg-black/95 backdrop-blur-md p-3 shadow-2xl`}
        >
          <div className={`font-mono text-[9px] tracking-[0.3em] text-${meta.accent} mb-1.5`}>
            {data.label?.toUpperCase() || wtype.toUpperCase()}
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            {TOOLTIPS[wtype] || data.description}
          </p>
        </div>
      )}
    </div>
  );
}

export default function WalletsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // Load once on mount. Any refresh (e.g. after a deposit / withdraw) happens
    // via the parent triggering a re-mount, not through a re-fetch — this keeps
    // the component effect-loop-free (see bug 2026-02-11: parent-passed callback
    // recreated on each parent render caused an infinite fetch loop).
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/wallet/me/v2`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div
        data-testid="wallets-panel-error"
        className="rounded-sm border border-red-400/40 bg-red-500/5 p-4 font-mono text-xs text-red-300"
      >
        Falha ao carregar carteiras: {err}
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div
        data-testid="wallets-panel-loading"
        className="rounded-sm border border-primary/20 bg-black/40 p-6 grid place-items-center text-primary font-mono text-[10px] tracking-widest"
      >
        <RefreshCw className="h-4 w-4 animate-spin mb-2" />
        CARREGANDO CARTEIRAS...
      </div>
    );
  }

  const wallets = data.wallets || {};
  const total =
    (wallets.entry_token?.amount_cents || 0) +
    (wallets.prize?.amount_cents || 0) +
    (wallets.bonus?.amount_cents || 0);

  return (
    <div data-testid="wallets-panel" className="space-y-3">
      {/* Summary bar */}
      <div className="rounded-sm border border-primary/40 bg-black/60 p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[9px] tracking-[0.3em] text-primary/70">SALDO TOTAL DISPONÍVEL</div>
          <div className="font-display font-black text-white text-2xl md:text-3xl tracking-tighter mt-0.5">
            {fmt(total)}{" "}
            <span className="text-primary text-base md:text-lg">USDT</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[9px] tracking-[0.3em] text-emerald-300/70">SACÁVEL AGORA</div>
          <div
            data-testid="wallet-withdrawable-total"
            className="font-display font-black text-emerald-300 text-xl md:text-2xl tracking-tighter mt-0.5"
          >
            {fmt(data.withdrawable_total_cents)}
          </div>
        </div>
      </div>

      {/* 4 wallet cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {WALLET_ORDER.map((wt) => (
          <WalletCard key={wt} wtype={wt} data={wallets[wt] || { amount_cents: 0 }} />
        ))}
      </div>

      {/* Compact rule reminder */}
      <div
        data-testid="wallet-rule-notice"
        className="rounded-sm border border-primary/20 bg-black/30 px-3 py-2 flex items-start gap-2"
      >
        <Info className="h-3.5 w-3.5 text-primary/70 mt-[2px] shrink-0" />
        <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">
          Depósitos viram{" "}
          <span className="text-cyan-300">Entry Tokens</span> — usados só para inscrição em torneios.
          Apenas o{" "}
          <span className="text-emerald-300">Saldo de Prêmios</span> pode ser sacado em USDT.
        </p>
      </div>
    </div>
  );
}
